import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Ban, Download, Edit, Mail, Printer } from 'lucide-react';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import AttachmentsListView from '../../components/AttachmentsListView';
import CustomerPaymentSendModal from '../../components/CustomerPaymentSendModal';
import VoidConfirmationModal from '../../components/VoidConfirmationModal';
import {
  getCustomerPaymentById,
  voidCustomerPayment,
  downloadCustomerPaymentPdf,
} from '../../utils/customerPaymentApi';
import { printCustomerPayment } from '../../utils/printCustomerPayment';
import { getCustomerPaymentAttachments } from '../../utils/paymentAttachmentApi';
import type { CustomerPayment } from '../../types/customerPayment';
import type { Attachment } from '../../types/attachment';
import { ApiValidationError } from '../../types/auth';
import {
  canEditCustomerPayment,
  canEditPaymentTransaction,
  canVoidPaymentTransaction,
  isPaymentVoided,
} from '../../utils/salesTransactionPermissions';
import { toast } from '../../utils/toast';
import { customerPaymentVoidCopy } from '../../utils/voidConfirmationCopy';
import { useSalesTransactionsHub } from './SalesTransactionsHubContext';
import {
  PreviewActionFooter,
  PreviewFinancialHero,
  PreviewLinkedList,
  PreviewMetaGrid,
  PreviewMetaItem,
  PreviewSectionHeading,
  PreviewSkeleton,
  previewActionClasses,
  previewBackLabelFromPath,
} from '../../components/transactions/TransactionPreviewShared';

const PAYMENT_VOID_REASON_MAX = 500;

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const paymentStatusColor = (status: string) => {
  switch (status.toUpperCase()) {
    case 'POSTED':
      return 'bg-green-100 text-green-800';
    case 'VOIDED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const SalesPaymentPreview = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useSalesTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');
  const routeState = location.state as { returnTo?: string } | null;

  const paymentId = id != null ? Number(id) : Number.NaN;

  const [payment, setPayment] = useState<CustomerPayment | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);

  const close = useCallback(() => {
    if (routeState?.returnTo) {
      navigate(routeState.returnTo, { preventScrollReset: true });
      return;
    }
    navigate({ pathname: '/sales-transactions', search: listSearch }, { preventScrollReset: true });
  }, [listSearch, navigate, routeState?.returnTo]);

  const loadPayment = useCallback(async () => {
    if (!id || Number.isNaN(paymentId)) {
      setError('Invalid payment ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [data, paymentAttachments] = await Promise.all([
        getCustomerPaymentById(paymentId),
        getCustomerPaymentAttachments(paymentId).catch(() => []),
      ]);
      setPayment(data);
      setAttachments(paymentAttachments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment');
      setPayment(null);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [id, paymentId]);

  useEffect(() => {
    void loadPayment();
  }, [loadPayment]);

  const openEdit = () => {
    if (!payment || !canEditPaymentTransaction(payment)) return;
    navigate(
      { pathname: `/sales-transactions/payment/${payment.id}/allocations`, search: listSearch },
      {
        preventScrollReset: true,
        state: {
          returnToPreview: true,
          returnTo: routeState?.returnTo,
        },
      }
    );
  };

  const openInvoice = (invoiceId: number) => {
    const returnTo = `/sales-transactions/view/payment/${payment?.id}${
      listSearch ? `?${listSearch}` : ''
    }`;
    navigate(
      {
        pathname: `/sales-transactions/view/invoice/${invoiceId}`,
        search: listSearch,
      },
      { preventScrollReset: true, state: { returnTo } }
    );
  };

  const openVoidModal = () => {
    setVoidError(null);
    setShowVoidModal(true);
  };

  const cancelVoidModal = () => {
    setShowVoidModal(false);
    setVoidError(null);
  };

  const confirmVoid = async (reason: string) => {
    if (!payment || !canVoidPaymentTransaction(payment)) return;

    try {
      setVoiding(true);
      setVoidError(null);
      const voided = await voidCustomerPayment(payment.id, {
        void_reason: reason || null,
      });
      refetchList();
      setPayment(voided);
      setShowVoidModal(false);
      toast.success(`${voided.payment_number} voided.`);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setVoidError(err.message);
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to void payment');
      }
    } finally {
      setVoiding(false);
    }
  };

  const handlePrint = async () => {
    if (!payment) return;
    try {
      setActionLoading(true);
      await printCustomerPayment(payment);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to print payment receipt');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!payment) return;
    try {
      setActionLoading(true);
      await downloadCustomerPaymentPdf(payment.id, `${payment.payment_number}.pdf`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download payment receipt PDF');
    } finally {
      setActionLoading(false);
    }
  };

  const status = payment?.status ?? 'POSTED';
  const totalApplied = payment
    ? payment.allocations.reduce((sum, alloc) => sum + parseFloat(alloc.amount_applied), 0)
    : 0;

  const backLink = routeState?.returnTo
    ? {
        label: previewBackLabelFromPath(routeState.returnTo),
        onClick: () => navigate(routeState.returnTo!, { preventScrollReset: true }),
      }
    : undefined;

  const actionFooter =
    payment && !loading && !error ? (
      <PreviewActionFooter>
        {canEditPaymentTransaction(payment) && (
          <button
            type="button"
            onClick={openEdit}
            disabled={actionLoading}
            className={previewActionClasses.primary}
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        )}
        {canVoidPaymentTransaction(payment) && (
          <button
            type="button"
            onClick={openVoidModal}
            disabled={actionLoading || voiding}
            className={previewActionClasses.danger}
          >
            <Ban className="h-4 w-4" />
            Void
          </button>
        )}
        <button
          type="button"
          onClick={() => void handlePrint()}
          disabled={actionLoading}
          className={previewActionClasses.secondary}
        >
          <Printer className="h-4 w-4" />
          Print
        </button>
        <button
          type="button"
          onClick={() => void handleDownloadPdf()}
          disabled={actionLoading}
          className={previewActionClasses.secondary}
        >
          <Download className="h-4 w-4" />
          Download
        </button>
        {canEditCustomerPayment() && (
          <button
            type="button"
            onClick={() => setShowSendModal(true)}
            disabled={actionLoading}
            className={previewActionClasses.secondary}
          >
            <Mail className="h-4 w-4" />
            Send
          </button>
        )}
        <button type="button" onClick={close} className={previewActionClasses.close}>
          Close
        </button>
      </PreviewActionFooter>
    ) : undefined;

  return (
    <>
    <TransactionOverlay
      variant="preview"
      titleMono
      title={payment?.payment_number ?? 'Payment'}
      subtitle={
        payment
          ? `${payment.customer?.display_name ?? '—'} · ${formatDate(payment.txn_date)}`
          : 'Loading…'
      }
      backLink={backLink}
      footer={actionFooter}
      onClose={close}
    >
      {loading ? (
        <PreviewSkeleton />
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : payment ? (
        <div className="space-y-5">
          <div>
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${paymentStatusColor(status)}`}
            >
              {formatLabel(status)}
            </span>
          </div>

          <PreviewFinancialHero
            amounts={[
              { label: 'Amount', amount: payment.total_amount },
              { label: 'Applied', amount: payment.applied_amount, tone: 'paid' },
              {
                label: 'Unapplied',
                amount: payment.unapplied_amount,
                tone: parseFloat(payment.unapplied_amount) > 0 ? 'owed' : 'default',
              },
            ]}
          />

          <PreviewMetaGrid>
            <PreviewMetaItem label="Customer">
              <span className="font-medium">{payment.customer?.display_name ?? '—'}</span>
            </PreviewMetaItem>
            <PreviewMetaItem label="Method">{formatLabel(payment.payment_method)}</PreviewMetaItem>
            {payment.ref_number ? (
              <PreviewMetaItem label="Reference">{payment.ref_number}</PreviewMetaItem>
            ) : null}
          </PreviewMetaGrid>

          {isPaymentVoided(payment) && payment.voided_at && (
            <div className="text-sm text-gray-600 space-y-1 rounded-lg border border-red-100 bg-red-50/50 p-3">
              <p>
                <span className="font-medium text-gray-700">Voided:</span>{' '}
                {formatDate(payment.voided_at)}
              </p>
              {payment.voided_by && (
                <p>
                  <span className="font-medium text-gray-700">By:</span>{' '}
                  {payment.voided_by.name ||
                    payment.voided_by.email ||
                    `User #${payment.voided_by.id}`}
                </p>
              )}
              {payment.void_reason && (
                <p>
                  <span className="font-medium text-gray-700">Reason:</span> {payment.void_reason}
                </p>
              )}
            </div>
          )}

          {payment.allocations.length > 0 && (
            <div>
              <PreviewSectionHeading>Allocations</PreviewSectionHeading>
              <PreviewLinkedList
                rows={payment.allocations.map((alloc) => ({
                  id: alloc.id,
                  title: alloc.invoice?.invoice_number ?? `Invoice #${alloc.invoice_id}`,
                  amount: alloc.amount_applied,
                  onClick: () => openInvoice(alloc.invoice_id),
                }))}
                footerLabel="Total applied"
                footerAmount={String(totalApplied)}
              />
            </div>
          )}

          {attachments.length > 0 && <AttachmentsListView attachments={attachments} />}
        </div>
      ) : null}
    </TransactionOverlay>

    {payment && (
      <CustomerPaymentSendModal
        payment={payment}
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        onSent={(updated) => setPayment(updated)}
      />
    )}
    {payment && (
      <VoidConfirmationModal
        isOpen={showVoidModal}
        {...customerPaymentVoidCopy(payment.payment_number)}
        confirmText="Void payment"
        showReasonField
        reasonMaxLength={PAYMENT_VOID_REASON_MAX}
        isSubmitting={voiding}
        error={voidError}
        onCancel={cancelVoidModal}
        onConfirm={confirmVoid}
      />
    )}
    </>
  );
};

export default SalesPaymentPreview;
